import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Stack,
  CardActions,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  DirectionsCar as CarIcon,
  FilterList as FilterIcon,
  Person as PersonIcon,
  CalendarToday as CalendarTodayIcon,
  CheckCircle as CheckCircleIcon,
  Engineering as EngineeringIcon,
  ArrowForward as ArrowForwardIcon,
  Pending as PendingIcon,
  Cancel as CancelIcon,
  LowPriority as LowPriorityIcon,
  PriorityHigh as PriorityHighIcon,
  Error as ErrorIcon,
  ReportProblem as ReportProblemIcon,
  Swipe as SwipeIcon,
  Refresh as RefreshIcon,
  Build as BuildIcon,
  SwapHoriz as SwapHorizIcon,
  Handyman as HandymanIcon,
  MoreHoriz as MoreHorizIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { task } from '../../../utils/api';
import { TASK_STATUS, TASK_PRIORITY, TASK_TYPE } from '../../../constants/task';
import { format } from 'date-fns';
import { useAuth } from '../../../components/auth/AuthModule';
import { toast } from 'react-hot-toast';
import { Link, useNavigate } from 'react-router-dom';

const CarView = () => {
  const { t } = useTranslation('workshop');
  const [groupedTasks, setGroupedTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCars, setExpandedCars] = useState({});
  const [statusFilter, setStatusFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchGroupedTasks();
  }, []);

  const fetchGroupedTasks = async () => {
    try {
      setLoading(true);
      const data = await task.getTasksByCarGrouped();
      console.log('Grouped tasks data:', data);
      
      // Check if tasks array exists in each group
      data.forEach((group, index) => {
        console.log(`Group ${index} (${group.carInfo.name}) tasks:`, group.tasks);
      });
      
      // Sort the data alphabetically by car name
      const sortedData = [...data].sort((a, b) => {
        const nameA = a.carInfo?.name?.toLowerCase() || '';
        const nameB = b.carInfo?.name?.toLowerCase() || '';
        return nameA.localeCompare(nameB);
      });
      
      setGroupedTasks(sortedData);
    } catch (error) {
      console.error('Error fetching grouped tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case TASK_STATUS.PENDING:
        return 'warning';
      case TASK_STATUS.IN_PROGRESS:
        return 'info';
      case TASK_STATUS.COMPLETED:
        return 'success';
      case TASK_STATUS.CANCELLED:
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case TASK_STATUS.PENDING:
        return <PendingIcon fontSize="small" />;
      case TASK_STATUS.IN_PROGRESS:
        return <EngineeringIcon fontSize="small" />;
      case TASK_STATUS.COMPLETED:
        return <CheckCircleIcon fontSize="small" />;
      case TASK_STATUS.CANCELLED:
        return <CancelIcon fontSize="small" />;
      default:
        return null;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case TASK_PRIORITY.LOW:
        return 'success';
      case TASK_PRIORITY.MEDIUM:
        return 'info';
      case TASK_PRIORITY.HIGH:
        return 'warning';
      case TASK_PRIORITY.URGENT:
        return 'error';
      default:
        return 'default';
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case TASK_PRIORITY.LOW:
        return <LowPriorityIcon />;
      case TASK_PRIORITY.MEDIUM:
        return <PriorityHighIcon />;
      case TASK_PRIORITY.HIGH:
        return <ReportProblemIcon />;
      case TASK_PRIORITY.URGENT:
        return <ErrorIcon />;
      default:
        return <LowPriorityIcon />;
    }
  };

  const getTaskTypeIcon = (type) => {
    switch (type) {
      case TASK_TYPE.MAINTENANCE:
        return <BuildIcon fontSize="small" />;
      case TASK_TYPE.REPLACEMENT:
        return <SwapHorizIcon fontSize="small" />;
      case TASK_TYPE.FIX:
        return <HandymanIcon fontSize="small" />;
      case TASK_TYPE.OTHER:
        return <MoreHorizIcon fontSize="small" />;
      default:
        return <BuildIcon fontSize="small" />;
    }
  };

  const handleAccordionChange = (carId) => (event, isExpanded) => {
    setExpandedCars(prev => ({ ...prev, [carId]: isExpanded }));
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const getFilteredTasks = (tasks) => {
    if (!tasks || !Array.isArray(tasks)) {
      console.log('Tasks is not an array:', tasks);
      return [];
    }
    
    console.log('Tasks before filtering:', tasks);
    console.log('Current status filter:', statusFilter);
    
    let filteredTasks = tasks;
    
    // Apply status filter if not "all"
    if (statusFilter !== 'all') {
      filteredTasks = tasks.filter(task => task.status === statusFilter);
    }
    
    // Sort tasks so completed tasks appear at the end
    filteredTasks.sort((a, b) => {
      if (a.status === TASK_STATUS.COMPLETED && b.status !== TASK_STATUS.COMPLETED) {
        return 1; // a comes after b
      } else if (a.status !== TASK_STATUS.COMPLETED && b.status === TASK_STATUS.COMPLETED) {
        return -1; // a comes before b
      } else {
        // If both are completed or both are not completed, sort by due date (if available)
        if (a.dueDate && b.dueDate) {
          return new Date(a.dueDate) - new Date(b.dueDate);
        } else if (a.dueDate) {
          return -1; // a comes first if it has a due date
        } else if (b.dueDate) {
          return 1; // b comes first if it has a due date
        }
        return 0; // maintain original order
      }
    });
    
    console.log('Filtered tasks:', filteredTasks);
    return filteredTasks;
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      await task.update(taskId, { status: newStatus });
      toast.success(t('success.taskUpdated'));
      fetchGroupedTasks(); // Refresh data
    } catch (error) {
      console.error('Error updating task status:', error);
      toast.error(t('error.updateTask'));
    }
  };
  
  const isTaskAssignedToCurrentUser = (taskAssignedTo) => {
    return user && taskAssignedTo && (taskAssignedTo._id === user._id);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 2,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Typography variant="h5" component="h2">
          {t('carView.title')}
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button 
            variant="outlined" 
            startIcon={<FilterIcon />}
            onClick={toggleFilters}
            size="small"
          >
            {showFilters ? t('common.hideFilters') : t('common.showFilters')}
          </Button>
          <Tooltip title={t('common.refresh')}>
            <IconButton onClick={fetchGroupedTasks} size="small" color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {showFilters && (
        <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 2 }}>
          <FormControl fullWidth variant="outlined" size="small">
            <InputLabel id="status-filter-label">{t('carView.filterByStatus')}</InputLabel>
            <Select
              labelId="status-filter-label"
              id="status-filter"
              value={statusFilter}
              onChange={handleStatusFilterChange}
              label={t('carView.filterByStatus')}
            >
              <MenuItem value="all">{t('carView.allTasks')}</MenuItem>
              <MenuItem value={TASK_STATUS.PENDING}>{t(`status.${TASK_STATUS.PENDING}`)}</MenuItem>
              <MenuItem value={TASK_STATUS.IN_PROGRESS}>{t(`status.${TASK_STATUS.IN_PROGRESS}`)}</MenuItem>
              <MenuItem value={TASK_STATUS.COMPLETED}>{t(`status.${TASK_STATUS.COMPLETED}`)}</MenuItem>
              <MenuItem value={TASK_STATUS.CANCELLED}>{t(`status.${TASK_STATUS.CANCELLED}`)}</MenuItem>
            </Select>
          </FormControl>
        </Box>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <CircularProgress />
        </Box>
      ) : groupedTasks.length === 0 ? (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '200px',
          bgcolor: 'background.paper',
          borderRadius: 2,
          p: 3,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        }}>
          <CarIcon sx={{ fontSize: '3rem', color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t('carView.noCarsFound')}
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center">
            {t('carView.addCarFirst')}
          </Typography>
        </Box>
      ) : (
        groupedTasks.map((group) => (
          <Accordion
            key={group._id}
            expanded={expandedCars[group._id]}
            onChange={handleAccordionChange(group._id)}
            sx={{ 
              mb: 2,
              borderRadius: '8px',
              overflow: 'hidden',
              '&:before': {
                display: 'none',
              },
              boxShadow: 'none'
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ 
                '& .MuiAccordionSummary-content': { 
                  margin: '8px 0',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 1
                },
                bgcolor: 'rgb(32, 32, 32)',
              }}
            >
              <Box 
                display="flex" 
                alignItems="center" 
                p={{ xs: 1, sm: 2, md: 3 }}
                width={{ xs: '100%', sm: 'auto' }}
              >
                <CarIcon color="primary" sx={{ mr: 1.5, fontSize: '1.5rem' }} />
                <Tooltip title={group.carInfo?.name || t('common.unknown')}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 'bold',
                      whiteSpace: 'nowrap',
                      maxWidth: { xs: '180px', sm: '200px', md: '250px' }
                    }}
                  >
                    {group.carInfo?.name || t('common.unknown')}
                  </Typography>
                </Tooltip>
              </Box>
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  flexWrap: 'wrap',
                  gap: 1,
                  ml: { xs: 0, sm: 'auto' }, 
                  mr: { xs: 0, sm: 2 },
                  width: { xs: '100%', sm: 'auto' },
                  mt: { xs: 1, sm: 0 }
                }}
              >
                <Chip 
                  label={`${t('carView.totalTasks')}: ${group.totalTasks || 0}`} 
                  color="primary" 
                  size="small" 
                  variant="outlined"
                />
                <Chip 
                  label={t('carView.pendingTasks', { count: group.pendingTasks || 0 })} 
                  color="warning" 
                  size="small" 
                  variant="outlined"
                />
                <Chip 
                  label={t('carView.completedTasks', { count: group.completedTasks || 0 })} 
                  color="success" 
                  size="small" 
                  variant="outlined"
                />
              </Box>
              <Box 
                sx={{ 
                  ml: 0, 
                  display: 'flex', 
                  alignItems: 'center', 
                  width: '100%',
                  mt: { xs: 1, sm: 1 }
                }}
              >
                <LinearProgress 
                  variant="determinate" 
                  value={group.totalTasks > 0 ? (group.completedTasks / group.totalTasks) * 100 : 0} 
                  sx={{ 
                    width: '100%', 
                    height: 8, 
                    borderRadius: 4,
                    marginBottom: 2,
                    backgroundColor: 'rgba(255,255,255,0.1)'
                  }} 
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 2, bgcolor: 'rgba(0,0,0,0.05)' }}>
              {getFilteredTasks(group.tasks || []).length > 2 && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                    <SwipeIcon fontSize="small" sx={{ mr: 0.5 }} />
                    {t('carView.scrollHint')}
                  </Typography>
                </Box>
              )}
              <Box 
                sx={{ 
                  overflowX: 'auto', 
                  pb: 1,
                  '&::-webkit-scrollbar': {
                    height: '8px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '4px',
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '4px',
                  },
                  scrollbarWidth: 'thin',
                  scrollbarColor: 'rgba(255, 255, 255, 0.2) rgba(255, 255, 255, 0.05)',
                }}
              >
                <Box 
                  sx={{ 
                    display: 'flex', 
                    flexDirection: 'row',
                    flexWrap: 'nowrap', 
                    gap: { xs: 2, sm: 3 }, 
                    pb: 1,
                    pl: 0.5,
                    pr: 0.5,
                    width: 'max-content',
                  }}
                >
                  {!group.tasks || getFilteredTasks(group.tasks).length === 0 ? (
                    <Box sx={{ width: '100%', p: 2, textAlign: 'center' }}>
                      <Typography color="text.secondary">
                        {t('carView.noTasksMatch')}
                      </Typography>
                    </Box>
                  ) : (
                    getFilteredTasks(group.tasks).map((task) => (
                      <Card 
                        key={task._id}
                        sx={{ 
                          width: { xs: 260, sm: 280, md: 300 },
                          minWidth: { xs: 260, sm: 280, md: 300 },
                          display: 'flex', 
                          flexDirection: 'column', 
                          height: { xs: 200, sm: 220 },
                          position: 'relative',
                          borderRadius: 2,
                          overflow: 'visible',
                          bgcolor: task.status === TASK_STATUS.COMPLETED ? 'rgba(76, 175, 80, 0.05)' : 'background.paper',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          opacity: task.status === TASK_STATUS.COMPLETED ? 0.7 : 1,
                          '&:hover': {
                            boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
                            transform: 'translateY(-4px)',
                            cursor: 'pointer',
                            opacity: 1 // Restore full opacity on hover
                          },
                          transition: 'all 0.3s ease-in-out'
                        }}
                        onClick={() => navigate(`/workshop/tasks/${task._id}`)}
                      >
                        {task.status === TASK_STATUS.COMPLETED && (
                          <Box 
                            sx={{ 
                              position: 'absolute',
                              top: 0,
                              right: 0,
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              zIndex: 1,
                              pointerEvents: 'none'
                            }}
                          >
                            <CheckCircleIcon 
                              sx={{ 
                                fontSize: { xs: '4rem', sm: '5rem' }, 
                                color: 'success.main',
                                opacity: 0.2
                              }} 
                            />
                          </Box>
                        )}
                        <Box 
                          sx={{ 
                            position: 'absolute',
                            bottom: 10,
                            right: 10,
                            zIndex: 2,
                            opacity: 0.7,
                            transition: 'opacity 0.2s ease-in-out',
                            '.MuiCard-root:hover &': {
                              opacity: 1
                            }
                          }}
                        >
                        </Box>
                        <CardContent sx={{ 
                          flexGrow: 1, 
                          pt: { xs: 1.5, sm: 2 }, 
                          pb: { xs: 0.5, sm: 1 }, 
                          px: { xs: 1.5, sm: 2 },
                          position: 'relative',
                          zIndex: 2,
                          background: 'linear-gradient(to bottom, rgba(255,255,255,0.05) 0%, rgba(0,0,0,0) 100%)'
                        }}>
                          <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column',
                            position: 'relative',
                            mb: 1
                          }}>
                            <Tooltip title={task.title} placement="top">
                              <Typography 
                                variant="h6" 
                                component="h3" 
                                sx={{ 
                                  mb: 1, 
                                  fontWeight: 'bold',
                                  fontSize: { xs: '1rem', sm: '1.25rem' }
                                }}
                              >
                                {task.title.length > 22 
                                  ? `${task.title.slice(0, 22)}...` 
                                  : task.title}
                              </Typography>
                            </Tooltip>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                              <Chip 
                                icon={getTaskTypeIcon(task.type || TASK_TYPE.MAINTENANCE)}
                                label={t(`taskType.${task.type || TASK_TYPE.MAINTENANCE}`)}
                                variant="outlined"
                                size="small" 
                                sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                              />
                              <Chip 
                                label={t(`priority.${task.priority}`)} 
                                color={getPriorityColor(task.priority)} 
                                size="small"
                                sx={{ 
                                  mr: 0.5,
                                  fontSize: { xs: '0.7rem', sm: '0.75rem' }
                                }}
                              />
                            </Box>
                            <Typography 
                              variant="body2" 
                              color="text.secondary" 
                              sx={{ 
                                mb: 1,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                fontSize: { xs: '0.75rem', sm: '0.875rem' }
                              }}
                            >
                              {task.description}
                            </Typography>
                          </Box>
                          <Box sx={{ mt: 'auto' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                              <PersonIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary', fontSize: { xs: '0.9rem', sm: '1rem' } }} />
                              <Typography 
                                variant="body2" 
                                color="text.secondary" 
                                noWrap 
                                sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem' } }}
                              >
                                {task.assignedTo?.name || task.assignedTo?.username || t('common.unknown')}
                              </Typography>
                            </Box>
                            {task.dueDate && (
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <CalendarTodayIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary', fontSize: { xs: '0.9rem', sm: '1rem' } }} />
                                <Typography 
                                  variant="body2" 
                                  color="text.secondary" 
                                  sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem' } }}
                                >
                                  {new Date(task.dueDate).toLocaleDateString()}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
        ))
      )}
    </Box>
  );
};

export default CarView; 